import toast from 'react-hot-toast';
import { isAfter, isPast, isSameDay, isSameHour, isSameMinute } from 'date-fns';
import cn from 'clsx';

import DateRangePicker, {
  DateRangePickerState,
} from '@components/elements/DatePicker/Range';
import TimePicker from '@components/elements/TimePicker';
import Toggle from '@components/elements/Toggle';
import { mergeDateTime } from '@utils/Date';

export interface PollPeriod extends DateRangePickerState {
  immediate?: boolean;
  startTime: Date;
  endTime: Date;
}

export interface PollPeriodInputProps {
  value: PollPeriod;
  onChange: (value: PollPeriod) => void;
  isEdit: boolean;
  allowImmediate?: boolean;
  neon?: string;
  isPledge?: boolean;
}

const PollPeriodInput: React.FC<PollPeriodInputProps> = ({
  value,
  onChange,
  isEdit,
  isPledge = false,
  allowImmediate = true,
  neon,
}) => {
  return (
    <div className="flex flex-col w-full">
      <div className="w-full mb-4 relative">
        <DateRangePicker
          focusedInput={
            value.focusedInput
              ? ['startDate', 'endDate'].includes(value.focusedInput)
                ? value.focusedInput
                : 'startDate'
              : 'startDate'
          }
          startDate={value.startDate}
          endDate={value.endDate}
          isPledge={isPledge}
          neon={neon}
          nowEl={
            allowImmediate && !isEdit ? (
              <div
                className="text-center ml-2 mr-3 relative"
                style={{
                  fontSize: 50,
                  lineHeight: 1.3,
                  border: `solid 2px ${neon}`,
                  width: 67,
                  minWidth: 67,
                  height: 67,
                  color: neon,
                }}
                onClick={(v) => {
                  onChange({
                    ...value,
                    immediate: !value.immediate,
                  });
                }}
              >
                <div
                  className="absolute text-lg font-bold"
                  style={{ top: -43, left: 10, color: neon }}
                >
                  Now
                </div>
                {!!value.immediate ? 'X' : ''}
              </div>
            ) : // <div
            //   className={cn(
            //     'flex flex-row px-2 py-4 mb-2 rounded-lg bg-white items-between border-2 border-white',
            //     {
            //       'border-magenta': value.immediate,
            //     }
            //   )}
            // >
            //   <h3
            //     className={cn('mr-4 text-lg w-full text-blue', {
            //       'text-magenta': value.immediate,
            //     })}
            //   >
            //     Start immediately
            //   </h3>
            //   <div className="flex items-center">
            //     <Toggle
            //       value={!!value.immediate}
            //       onChange={(v) => {
            //         onChange({
            //           ...value,
            //           immediate: v,
            //         });
            //       }}
            //       activeClassName="bg-magenta"
            //     />
            //   </div>
            // </div>
            undefined
          }
          onChange={(data) => {
            let error;
            if (isEdit) {
              if (
                data.startDate &&
                value.endTime &&
                !isSameDay(data.startDate, value.startDate) &&
                !value.immediate &&
                isPast(mergeDateTime(data.startDate, value.startTime))
              ) {
                error = 'Start date must be in the future.';
              }
            } else {
              if (
                data.startDate &&
                value.endTime &&
                !value.immediate &&
                isPast(mergeDateTime(data.startDate, value.startTime))
              ) {
                error = 'Start date must be in the future.';
              }
            }

            if (
              data.endDate &&
              value.endTime &&
              isPast(mergeDateTime(data.endDate, value.endTime))
            ) {
              error = 'End date must be in the future.';
            }

            if (error) toast.error(error);
            if (!error) {
              const payload = { ...value };
              payload.focusedInput = data.focusedInput;
              if (data.startDate) payload.startDate = data.startDate;
              if (data.endDate) payload.endDate = data.endDate;
              if (isAfter(payload.startDate, payload.endDate)) {
                payload.endDate = payload.startDate;
              }
              onChange(payload);
            }
          }}
        />
      </div>
      <div className="grid n-col-2">
        <div>
          <h4
            style={{
              pointerEvents: value.immediate ? 'none' : 'all',
              opacity: value.immediate ? 0 : 1,
              color: neon,
            }}
            className="mb-2 text-lg px-4"
          >
            {isPledge ? 'Pledge' : 'Poll'} Start Time
          </h4>

          <div
            style={{
              pointerEvents: value.immediate ? 'none' : 'all',
              opacity: value.immediate ? 0 : 1,
            }}
          >
            <TimePicker
              disabled={!!value.immediate}
              neon={neon}
              onChange={(date) => {
                if (isEdit) {
                  if (
                    value.startDate &&
                    date &&
                    !(
                      isSameHour(date, value.startDate) &&
                      isSameMinute(date, value.startDate)
                    ) &&
                    isPast(mergeDateTime(value.startDate, date))
                  ) {
                    return toast.error('Start time must be in the future.');
                  }
                } else {
                  if (
                    value.startDate &&
                    date &&
                    isPast(mergeDateTime(value.startDate, date))
                  ) {
                    return toast.error('Start time must be in the future.');
                  }
                }
                date.setSeconds(0);
                onChange({
                  ...value,
                  startTime: date,
                });
              }}
              value={value.startTime}
            />
          </div>
        </div>
        <div>
          <h4 className="mb-2 text-lg px-4" style={{ color: neon }}>
            {isPledge ? 'Pledge' : 'Poll'} End Time
          </h4>
          <TimePicker
            neon={neon}
            onChange={(date) => {
              if (isEdit) {
                if (
                  value.endDate &&
                  date &&
                  !(
                    isSameHour(date, value.endDate) &&
                    isSameMinute(date, value.endDate)
                  ) &&
                  isPast(mergeDateTime(value.endDate, date))
                ) {
                  return toast.error('End time must be in the future.');
                }
              } else {
                if (
                  value.endDate &&
                  date &&
                  isPast(mergeDateTime(value.endDate, date))
                ) {
                  return toast.error('End time must be in the future.');
                }
              }
              date.setSeconds(0);
              onChange({
                ...value,
                endTime: date,
              });
            }}
            value={value.endTime}
          />
        </div>
      </div>
    </div>
  );
};

export default PollPeriodInput;
